import { useMemo } from 'react';
import { ExtendedProductListingProduct } from 'shared/types/product/Product';
import { Money } from 'types/entity/product';

interface PricingResult {
  displayPrice: Money | string;
  displayDiscountedPrice: Money | string | undefined;
  hasDiscount: boolean;
  discountPercentage: number;
  shouldUseStartingPrice: boolean;
  hasSaleStartingPrice: boolean;
  shouldHidePrice: boolean;
  isStartingPrice: boolean;
  isSaleStartingPrice: boolean;
}

/**
 * Hook to handle product pricing logic, focusing on:
 * - Starting at pricing
 * - Sale starting at pricing
 */
export const useProductPricing = (product: ExtendedProductListingProduct, selectedVariantIndex = 0): PricingResult => {
  return useMemo(() => {
    // Check if we have sale starting at price
    const hasSaleStartingPrice =
      product.startingAtPrice !== undefined && product.startingAtPrice.salePrice !== undefined;

    // Determine if we should use "starting at" wording (for multiple variants)
    const hasMultipleVariants = product.variants.length > 1;
    const shouldUseStartingPrice = hasMultipleVariants;

    // Determine discount status and percentage
    let hasDiscount = false;
    const discountPercentage = hasSaleStartingPrice ? (product.startingAtPrice?.discountPercentage ?? 0) : 0;
    let displayPrice: Money | string;
    let displayDiscountedPrice: Money | string | undefined = undefined;

    // First priority: Use salesStartingAtPrice if available
    if (hasSaleStartingPrice && product.startingAtPrice?.salePrice) {
      hasDiscount = true;
      displayPrice = product.startingAtPrice.basePrice || '';
      displayDiscountedPrice = product.startingAtPrice.salePrice;
    }
    // Second priority: Use startingAtPrice if available
    else if (product.startingAtPrice) displayPrice = product.startingAtPrice.basePrice;
    // Last resort: Use selected variant price  (by default it's the master variant)
    else {
      const selectedVariant = product?.variants[selectedVariantIndex];
      displayPrice = selectedVariant?.price || { centAmount: 0, currencyCode: 'USD' };
    }

    // Determine if price should be hidden
    const shouldHidePrice =
      displayPrice === undefined ||
      displayPrice === '' ||
      (typeof displayPrice === 'object' && (!displayPrice.centAmount || displayPrice.centAmount === 0)) ||
      (product.startingAtPrice !== undefined &&
        (product.startingAtPrice.basePriceNumeric === 0 || product.startingAtPrice.basePriceNumeric === undefined));

    // Calculate additional pricing flags
    const isStartingPrice = shouldUseStartingPrice && !hasDiscount;
    const isSaleStartingPrice = shouldUseStartingPrice && hasDiscount;

    return {
      displayPrice,
      displayDiscountedPrice,
      hasDiscount,
      discountPercentage,
      shouldUseStartingPrice,
      hasSaleStartingPrice,
      shouldHidePrice,
      isStartingPrice,
      isSaleStartingPrice,
    };
  }, [product, selectedVariantIndex]);
};

export default useProductPricing;
